import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_COUNTS = ["1", "2-5", "6-10", "11-25", "26+"];
const PROPERTY_TYPES = ["Houses", "Flats / apartments", "HMOs / student lets", "Serviced / short-stay", "Communal areas"];
const SERVICES = ["End of tenancy cleans", "Mid-tenancy / inspection cleans", "Pre-let deep cleans", "Communal area cleaning", "Carpet cleaning", "Oven cleaning"];
const FREQUENCIES = ["As needed (each tenancy change)", "Weekly", "Fortnightly", "Monthly"];

const EMPTY = { name: "", company: "", email: "", phone: "", properties: "2-5", propertyTypes: [], services: [], frequency: FREQUENCIES[0], areas: "", message: "" };

const inputCls = "w-full rounded-lg border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600";

const Field = ({ label, children, required }) => (
  <label className="block space-y-1.5">
    <span className="text-sm font-semibold text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</span>
    {children}
  </label>
);

const ChipGroup = ({ label, options, value, onToggle, testId }) => (
  <fieldset className="space-y-2" data-testid={testId}>
    <legend className="text-sm font-semibold text-slate-700">{label}</legend>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const on = value.includes(opt);
        return (
          <button
            type="button"
            key={opt}
            aria-pressed={on}
            onClick={() => onToggle(opt)}
            className={`rounded-full border-2 px-4 py-2 text-sm font-medium transition-colors ${on ? "border-sky-600 bg-sky-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-sky-300"}`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </fieldset>
);

const SuccessPanel = ({ onReset }) => (
  <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-10 text-center" data-testid="contract-enquiry-success">
    <CheckCircle2 className="mx-auto mb-4 h-14 w-14 text-emerald-600" />
    <h3 className="mb-2 text-2xl font-bold text-slate-900">Thank you, we've received your enquiry</h3>
    <p className="mb-6 text-slate-600">We'll review your portfolio details and get back to you with a contract price.</p>
    <button type="button" onClick={onReset} className="font-semibold text-sky-700 hover:underline">Send another enquiry</button>
  </div>
);

const ContractEnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => (opt) => setForm((f) => ({ ...f, [key]: f[key].includes(opt) ? f[key].filter((o) => o !== opt) : [...f[key], opt] }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contract-enquiries`, form);
      setStatus("sent");
      setForm(EMPTY);
      document.getElementById("contract-enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.status === 429 ? err.response.data.detail : "Sorry, something went wrong. Please call us on 0749 286 5011.");
    }
  };

  return (
    <section id="contract-enquiry" className="scroll-mt-32 bg-slate-50 py-16" data-testid="contract-enquiry-section">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-3 text-center text-3xl font-bold text-slate-900">Request a Contract Price</h2>
        <p className="mb-10 text-center text-slate-600">Tell us about your portfolio and we'll prepare a tailored price for ongoing cleaning.</p>
        {status === "sent" ? <SuccessPanel onReset={() => setStatus("idle")} /> : (
          <form onSubmit={submit} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-lg sm:p-8" data-testid="contract-enquiry-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Your name" required><input required maxLength={100} value={form.name} onChange={set("name")} className={inputCls} data-testid="contract-name-input" /></Field>
              <Field label="Company / agency"><input maxLength={150} value={form.company} onChange={set("company")} className={inputCls} data-testid="contract-company-input" /></Field>
              <Field label="Email" required><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contract-email-input" /></Field>
              <Field label="Phone" required><input required type="tel" minLength={6} maxLength={30} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contract-phone-input" /></Field>
              <Field label="Number of properties" required>
                <select value={form.properties} onChange={set("properties")} className={inputCls} data-testid="contract-properties-select">
                  {PROPERTY_COUNTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="How often" required>
                <select value={form.frequency} onChange={set("frequency")} className={inputCls} data-testid="contract-frequency-select">
                  {FREQUENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>
            <ChipGroup label="Property types" options={PROPERTY_TYPES} value={form.propertyTypes} onToggle={toggle("propertyTypes")} testId="contract-property-types" />
            <ChipGroup label="Services needed" options={SERVICES} value={form.services} onToggle={toggle("services")} testId="contract-services" />
            <Field label="Areas / postcodes covered"><input maxLength={200} value={form.areas} onChange={set("areas")} placeholder="e.g. Cathays, Roath, CF24" className={inputCls} data-testid="contract-areas-input" /></Field>
            <Field label="Anything else we should know?"><textarea maxLength={2000} rows={4} value={form.message} onChange={set("message")} className={inputCls} data-testid="contract-message-input" /></Field>
            {error && <p className="text-sm font-medium text-red-600" data-testid="contract-enquiry-error">{error}</p>}
            <p className="text-xs text-slate-500">We only use your details to reply to this enquiry. See our <a href="/privacy-policy" className="text-sky-700 underline">Privacy Policy</a>.</p>
            <button type="submit" disabled={status === "sending"} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-8 py-4 text-lg font-bold text-white shadow-lg transition-colors hover:bg-sky-700 disabled:opacity-60 sm:w-auto" data-testid="contract-enquiry-submit">
              {status === "sending" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Request contract price
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default ContractEnquiryForm;
